import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import VaultModalCloseButton from "../../components/vault/VaultModalCloseButton";
import {
  estimateOrganizerProcessingTariff,
  estimateTicketProcessingTariff,
  getTicketCardPaymentAmounts,
} from "../../utils/organizerProcessingTariff";

const DEFAULT_TICKET_PRICE = 15;
const DEFAULT_PARTICIPANTS = 100;
const ENTERPRISE_MIN_PLATFORM_FEE = 50;
const TRANSITION_DURATION_MS = 220;
export const COMMERCIAL_CONDITIONS_VERSION = "2026-10-23";

// Keep this edition unchanged after it has been referenced by an accepted
// organizer offer. Publish later revisions as a separate edition object.
const COMMERCIAL_CONDITIONS_COPY_2026_10_23 = {
  en: {
    title: "Commercial conditions",
    subtitle:
      "Platform commission, payment costs, and estimated proceeds.",
    directModeNote: "Approved organizers may receive a direct Stripe payment offer. For those events, ticket funds enter the organizer's Stripe account without a VaultGrid event-end hold. Automatic bank payouts follow Stripe's schedule; an organizer who chooses manual payouts starts them in Stripe Dashboard. The organizer pays Stripe's actual processing fees, and VaultGrid invoices any platform commission after the event and 72-hour complaint period. The calculator below estimates the standard payout mode; the organizer's accepted offer controls their terms.",
    closeAria: "Close commercial conditions",
    nav: {
      terms: "Commercial conditions",
      calculator: "Fee calculator",
      refunds: "Refund policy",
      howItWorks: "How it works",
    },
    terms: {
      title: "Commercial conditions",
      cards: [
        {
          badge: "Up to €300",
          heading: "Community",
          turnover: "Final net ticket turnover up to €300 per event",
          commission: "5%",
          commissionLabel: "platform commission on ticket sales",
          points: [
            "VaultGrid commission on ticket sales: 5% of net ticket turnover.",
            "Suitable for community events, workshops, and smaller paid formats.",
          ],
        },
        {
          badge: "Over €300 to €1,000",
          heading: "Growth",
          turnover: "Final net ticket turnover above €300 and up to €1,000 per event",
          commission: "4%",
          commissionLabel: "platform commission on ticket sales",
          points: [
            "VaultGrid commission on ticket sales: 4% of net ticket turnover.",
            "Suitable for recurring paid events and growing organizers.",
          ],
        },
        {
          badge: "Over €1,000",
          heading: "Scale",
          turnover: "Final net ticket turnover above €1,000 per event",
          commission: "3%",
          commissionLabel: "platform commission on ticket sales",
          points: [
            "VaultGrid commission on ticket sales: 3% of net ticket turnover, minimum €50 per event.",
            "Suitable for agencies, clubs, festivals, and larger productions.",
          ],
        },
      ],
    },
    calculator: {
      title: "Fee calculator",
      ticketPrice: "Ticket price",
      participants: "Participants",
      paymentMethod: "Payment method",
      card: "Online card",
      cash: "Cash",
      depositChoice: "How will the ticket be paid?",
      onePayment: "One payment",
      twoPayments: "Deposit + balance",
      depositPercent: "Deposit portion",
      depositSplit: "Deposit {{deposit}} · balance {{balance}}",
      commissionFormula: "{{rate}}% × {{turnover}}",
      commissionMinimumFormula: "{{rate}}% × {{turnover}} = {{calculated}}; minimum {{minimum}} applies",
      processingFormula: "1.9% + €0.25 per payment, rounded up to €0.10",
      processingCashFormula: "No card-processing cost for cash payments",
      processingSingle: "{{count}} payments × {{perTicket}} per ticket",
      processingSplit: "{{count}} tickets × ({{depositFee}} deposit + {{balanceFee}} balance)",
      selectedModel: "Selected commercial model",
      turnover: "Gross ticket turnover",
      platformCommission: "VaultGrid commission",
      serviceFee: "Card processing estimate",
      estimatedPayout: "You keep (estimate)",
      rangeLabels: {
        community: "Community",
        growth: "Growth",
        enterprise: "Scale",
      },
    },
    refunds: {
      title: "Refund policy",
      intro: "When a host creates a paid event, one refund policy is selected for that event. Participants see it before joining.",
      note: "If the organizer cancels the event or changes its date, time, or location after purchase, participants may receive a 100% ticket refund regardless of the selected policy. The organizer covers card-processing costs for successful payments.",
      options: [
        {
          id: "flexible",
          label: "Flexible",
          summary: "Best for socials, community events, and sports.",
          badge: "Recommended",
          rules: [
            { condition: "48h+ before event", outcome: "100% refund" },
            { condition: "12-48h before event", outcome: "50% refund" },
            { condition: "Less than 12h before event", outcome: "No refund" },
          ],
        },
        {
          id: "moderate",
          label: "Moderate",
          summary: "Balanced option for most planned paid events.",
          badge: "Balanced",
          rules: [
            { condition: "72h+ before event", outcome: "100% refund" },
            { condition: "Less than 72h before event", outcome: "No refund" },
          ],
        },
        {
          id: "strict",
          label: "Strict",
          summary: "Best for limited-capacity events.",
          badge: "Host-protected",
          rules: [
            { condition: "Non-refundable after booking", outcome: "" },
          ],
        },
      ],
    },
    howItWorks: {
      title: "How it works",
      steps: [
        {
          title: "Create your paid event",
          body: "Set the ticket price, number of places, and refund policy.",
          Icon: EventAvailableRoundedIcon,
        },
        {
          title: "Sell tickets",
          body: "On VaultGrid, you can sell tickets with online card payment or collect cash directly from participants.",
          Icon: PaymentsRoundedIcon,
        },
        {
          title: "Track sales and ticket analytics",
          body: "In the organizer panel you can review sold tickets, payment status, buyer records, and payout-related activity for each event.",
          Icon: QueryStatsRoundedIcon,
        },
        {
          title: "Standard payouts after the event",
          body: "In the standard payout mode, VaultGrid releases eligible funds after the event and 72-hour complaint period, once Stripe funds are available. Stripe and the receiving bank determine delivery time. Direct payout events follow the approved organizer offer instead.",
          Icon: ShieldRoundedIcon,
        },
      ],
      paymentNotes: [
        {
          title: "Online card payment",
          body: "The participant pays the ticket price online. The organizer covers payment-processing costs and any VaultGrid commission that applies under the accepted offer.",
          Icon: CalculateRoundedIcon,
        },
        {
          title: "Cash payment",
          body: "You collect cash directly from participants. Before publishing a cash-paid event, you place a deposit on the platform; the VaultGrid commission is later deducted from it.",
          Icon: TaskAltRoundedIcon,
        },
      ],
    },
  },
  ru: {
    title: "Коммерческие условия",
    subtitle:
      "Комиссия, расходы на платежи и примерный доход организатора.",
    directModeNote: "Одобренному организатору могут предложить прямые платежи через Stripe. Для таких событий выручка поступает на Stripe-аккаунт организатора без удержания VaultGrid до конца события. При автоматических выплатах Stripe переводит её в банк по графику; при ручных организатор запускает вывод сам в Stripe Dashboard. Организатор оплачивает фактическую комиссию Stripe, а VaultGrid выставляет счёт на свою комиссию после события и 72-часового срока для жалоб. Калькулятор ниже рассчитывает стандартный режим; условия конкретного организатора определяет принятое им предложение.",
    closeAria: "Закрыть коммерческие условия",
    nav: {
      terms: "Коммерческие условия",
      calculator: "Калькулятор комиссий",
      refunds: "Возвраты",
      howItWorks: "Как это работает",
    },
    terms: {
      title: "Коммерческие условия",
      cards: [
        {
          badge: "До 300 €",
          heading: "Сообщество",
          turnover: "Итоговый чистый оборот билетов до 300 € за событие",
          commission: "5%",
          commissionLabel: "комиссия платформы с продажи билетов",
          points: [
            "Комиссия VaultGrid с продажи билетов: 5% от чистого оборота билетов.",
            "Подходит для ивентов сообществ, воркшопов и небольших платных форматов.",
          ],
        },
        {
          badge: "Свыше 300 € до 1 000 €",
          heading: "Рост",
          turnover: "Итоговый чистый оборот билетов свыше 300 € и до 1 000 € включительно за событие",
          commission: "4%",
          commissionLabel: "комиссия платформы с продажи билетов",
          points: [
            "Комиссия VaultGrid с продажи билетов: 4% от чистого оборота билетов.",
            "Подходит для регулярных платных событий и растущих организаторов.",
          ],
        },
        {
          badge: "Свыше 1 000 €",
          heading: "Масштаб",
          turnover: "Итоговый чистый оборот билетов свыше 1 000 € за событие",
          commission: "3%",
          commissionLabel: "комиссия платформы с продажи билетов",
          points: [
            "Комиссия VaultGrid с продажи билетов: 3% от чистого оборота билетов, минимум 50 € за событие.",
            "Подходит для агентств, клубов, фестивалей и более крупных продакшенов.",
          ],
        },
      ],
    },
    calculator: {
      title: "Калькулятор комиссий",
      ticketPrice: "Цена билета",
      participants: "Количество участников",
      paymentMethod: "Способ оплаты",
      card: "Онлайн-карта",
      cash: "Наличные",
      depositChoice: "Как оплачивается билет?",
      onePayment: "Одним платежом",
      twoPayments: "Депозит + доплата",
      depositPercent: "Размер депозита",
      depositSplit: "Депозит {{deposit}} · доплата {{balance}}",
      commissionFormula: "{{rate}}% × {{turnover}}",
      commissionMinimumFormula: "{{rate}}% × {{turnover}} = {{calculated}}; действует минимум {{minimum}}",
      processingFormula: "1,9% + 0,25 € за платёж, округление вверх до 0,10 €",
      processingCashFormula: "При оплате наличными обработки картой нет",
      processingSingle: "{{count}} платежей × {{perTicket}} за билет",
      processingSplit: "{{count}} билетов × ({{depositFee}} за депозит + {{balanceFee}} за доплату)",
      selectedModel: "Выбранная коммерческая модель",
      turnover: "Оборот билетов",
      platformCommission: "Комиссия VaultGrid",
      serviceFee: "Обработка платежей картой",
      estimatedPayout: "Вам останется (оценка)",
      rangeLabels: {
        community: "Сообщество",
        growth: "Рост",
        enterprise: "Масштаб",
      },
    },
    refunds: {
      title: "Возвраты",
      intro: "При создании платного ивента хост выбирает одну политику возврата для этого ивента. Участники видят её до вступления.",
      note: "Если организатор отменит событие или после покупки изменит дату, время либо место, участник может вернуть 100% цены билета при любых выбранных правилах. Расходы на обработку успешных платежей картой оплачивает организатор.",
      options: [
        {
          id: "flexible",
          label: "Flexible",
          summary: "Лучше всего подходит для socials, community events и sports.",
          badge: "Рекомендуем",
          rules: [
            { condition: "За 48+ часов до ивента", outcome: "100% возврат" },
            { condition: "За 12-48 часов до ивента", outcome: "50% возврат" },
            { condition: "Менее чем за 12 часов до ивента", outcome: "Без возврата" },
          ],
        },
        {
          id: "moderate",
          label: "Moderate",
          summary: "Простой и понятный вариант.",
          badge: "Баланс",
          rules: [
            { condition: "За 72+ часа до ивента", outcome: "100% возврат" },
            { condition: "Менее чем за 72 часа до ивента", outcome: "Без возврата" },
          ],
        },
        {
          id: "strict",
          label: "Strict",
          summary: "Лучше всего для ивентов с ограниченным количеством мест.",
          badge: "Защита хоста",
          rules: [
            { condition: "После бронирования возврат не предусмотрен", outcome: "" },
          ],
        },
      ],
    },
    howItWorks: {
      title: "Как это работает",
      steps: [
        {
          title: "Создайте платное событие",
          body: "Укажите цену билета, количество мест и правила возврата.",
          Icon: EventAvailableRoundedIcon,
        },
        {
          title: "Продавайте билеты",
          body: "На VaultGrid можно продавать билеты с онлайн-оплатой картой или принимать наличные напрямую от участников.",
          Icon: PaymentsRoundedIcon,
        },
        {
          title: "Отслеживайте продажи и билетную аналитику",
          body: "В панели организатора вы видите проданные билеты, статусы оплат, данные покупателей и информацию, связанную с выплатами по каждому событию.",
          Icon: QueryStatsRoundedIcon,
        },
        {
          title: "Стандартная выплата после события",
          body: "В стандартном режиме VaultGrid освобождает допустимые средства после события и 72-часового срока для жалоб, когда средства доступны в Stripe. Срок зачисления зависит от Stripe и банка. Для прямых выплат действуют одобренные условия организатора.",
          Icon: ShieldRoundedIcon,
        },
      ],
      paymentNotes: [
        {
          title: "Онлайн-оплата картой",
          body: "Участник оплачивает билет онлайн. Организатор оплачивает обработку платежей и комиссию VaultGrid, если она предусмотрена принятым предложением.",
          Icon: CalculateRoundedIcon,
        },
        {
          title: "Оплата наличными",
          body: "Вы получаете наличные напрямую от участников. Перед публикацией такого события вы вносите депозит на платформе; затем из него вычитается комиссия VaultGrid.",
          Icon: TaskAltRoundedIcon,
        },
      ],
    },
  },
  sk: {
    title: "Komerčné podmienky",
    subtitle:
      "Provízia, náklady na platby a odhad tvojho výnosu.",
    directModeNote: "Schválenému organizátorovi možno ponúknuť priame platby cez Stripe. Pri týchto podujatiach tržby idú na účet Stripe organizátora bez zadržania VaultGridom do konca podujatia. Pri automatických výplatách ich Stripe pošle do banky podľa harmonogramu; pri manuálnych ich organizátor spustí sám v Stripe Dashboard. Organizátor hradí skutočné poplatky Stripe a VaultGrid fakturuje prípadnú províziu po podujatí a 72-hodinovej lehote na sťažnosti. Kalkulačka nižšie odhaduje štandardný režim; podmienky konkrétneho organizátora určuje jeho prijatá ponuka.",
    closeAria: "Zavrieť komerčné podmienky",
    nav: {
      terms: "Komerčné podmienky",
      calculator: "Kalkulačka poplatkov",
      refunds: "Vrátenie platieb",
      howItWorks: "Ako to funguje",
    },
    terms: {
      title: "Komerčné podmienky",
      cards: [
        {
          badge: "Do 300 €",
          heading: "Komunita",
          turnover: "Konečný čistý obrat z lístkov do 300 € za podujatie",
          commission: "5%",
          commissionLabel: "platformová provízia z predaja lístkov",
          points: [
            "Provízia VaultGrid z predaja lístkov: 5 % z čistého obratu z lístkov.",
            "Vhodné pre komunitné podujatia, workshopy a menšie platené formáty.",
          ],
        },
        {
          badge: "Nad 300 € do 1 000 €",
          heading: "Rast",
          turnover: "Konečný čistý obrat z lístkov nad 300 € až do 1 000 € vrátane za podujatie",
          commission: "4%",
          commissionLabel: "platformová provízia z predaja lístkov",
          points: [
            "Provízia VaultGrid z predaja lístkov: 4 % z čistého obratu z lístkov.",
            "Vhodné pre pravidelné platené podujatia a rastúcich organizátorov.",
          ],
        },
        {
          badge: "Nad 1 000 €",
          heading: "Veľký rozsah",
          turnover: "Konečný čistý obrat z lístkov nad 1 000 € za podujatie",
          commission: "3%",
          commissionLabel: "platformová provízia z predaja lístkov",
          points: [
            "Provízia VaultGrid z predaja lístkov: 3 % z čistého obratu z lístkov, najmenej 50 € za podujatie.",
            "Vhodné pre agentúry, kluby, festivaly a väčšie produkcie.",
          ],
        },
      ],
    },
    calculator: {
      title: "Kalkulačka poplatkov",
      ticketPrice: "Cena lístka",
      participants: "Počet účastníkov",
      paymentMethod: "Platobná metóda",
      card: "Online karta",
      cash: "Hotovosť",
      depositChoice: "Ako sa platí lístok?",
      onePayment: "Jednou platbou",
      twoPayments: "Depozit + doplatok",
      depositPercent: "Výška depozitu",
      depositSplit: "Depozit {{deposit}} · doplatok {{balance}}",
      commissionFormula: "{{rate}} % × {{turnover}}",
      commissionMinimumFormula: "{{rate}} % × {{turnover}} = {{calculated}}; platí minimum {{minimum}}",
      processingFormula: "1,9 % + 0,25 € za platbu, zaokrúhlené nahor na 0,10 €",
      processingCashFormula: "Pri hotovosti sa spracovanie kartou neúčtuje",
      processingSingle: "{{count}} platieb × {{perTicket}} za lístok",
      processingSplit: "{{count}} lístkov × ({{depositFee}} depozit + {{balanceFee}} doplatok)",
      selectedModel: "Vybraný komerčný model",
      turnover: "Obrat z lístkov",
      platformCommission: "Provízia VaultGrid",
      serviceFee: "Spracovanie platieb kartou",
      estimatedPayout: "Zostane ti (odhad)",
      rangeLabels: {
        community: "Komunita",
        growth: "Rast",
        enterprise: "Veľký rozsah",
      },
    },
    refunds: {
      title: "Vrátenie platieb",
      intro: "Pri vytváraní plateného podujatia si organizátor vyberá jedny storno podmienky. Účastníci ich uvidia ešte pred pripojením.",
      note: "Ak organizátor podujatie zruší alebo po kúpe zmení dátum, čas či miesto, účastník môže dostať späť 100 % ceny lístka bez ohľadu na zvolené storno podmienky. Náklady na spracovanie úspešných platieb kartou hradí organizátor.",
      options: [
        {
          id: "flexible",
          label: "Flexibilné",
          summary: "Najlepšie pre spoločenské, komunitné a športové podujatia.",
          badge: "Odporúčané",
          rules: [
            { condition: "Najmenej 48 h pred podujatím", outcome: "Vrátenie 100 %" },
            { condition: "12 až 48 h pred podujatím", outcome: "Vrátenie 50 %" },
            { condition: "Menej ako 12 h pred podujatím", outcome: "Bez vrátenia" },
          ],
        },
        {
          id: "moderate",
          label: "Stredné",
          summary: "Jednoduchá a zrozumiteľná voľba.",
          badge: "Vyvážené",
          rules: [
            { condition: "Najmenej 72 h pred podujatím", outcome: "Vrátenie 100 %" },
            { condition: "Menej ako 72 h pred podujatím", outcome: "Bez vrátenia" },
          ],
        },
        {
          id: "strict",
          label: "Prísne",
          summary: "Najlepšie pre podujatia s obmedzenou kapacitou.",
          badge: "Chráni hostiteľa",
          rules: [
            { condition: "Po rezervácii bez vrátenia platby", outcome: "" },
          ],
        },
      ],
    },
    howItWorks: {
      title: "Ako to funguje",
      steps: [
        {
          title: "Vytvor platené podujatie",
          body: "Nastav cenu lístka, počet miest a storno podmienky.",
          Icon: EventAvailableRoundedIcon,
        },
        {
          title: "Predávaj lístky",
          body: "Na VaultGride môžeš predávať lístky s online platbou kartou alebo prijímať hotovosť priamo od účastníkov.",
          Icon: PaymentsRoundedIcon,
        },
        {
          title: "Sleduj predaj a analytiku lístkov",
          body: "V paneli organizátora vidíš predané lístky, stav platieb, záznamy o kupujúcich aj údaje súvisiace s výplatami pre každé podujatie.",
          Icon: QueryStatsRoundedIcon,
        },
        {
          title: "Štandardná výplata po podujatí",
          body: "V štandardnom režime VaultGrid uvoľní oprávnené prostriedky po podujatí a 72-hodinovej lehote na sťažnosti, keď sú dostupné v Stripe. Čas pripísania závisí od Stripe a banky. Pri priamych výplatách platí schválená ponuka organizátora.",
          Icon: ShieldRoundedIcon,
        },
      ],
      paymentNotes: [
        {
          title: "Online platba kartou",
          body: "Účastník zaplatí cenu lístka online. Organizátor hradí spracovanie platieb a prípadnú províziu VaultGrid podľa prijatej ponuky.",
          Icon: CalculateRoundedIcon,
        },
        {
          title: "Platba v hotovosti",
          body: "Hotovosť prijímaš priamo od účastníkov. Pred zverejnením takého podujatia vložíš na platformu depozit, z ktorého sa neskôr odpočíta provízia VaultGrid.",
          Icon: TaskAltRoundedIcon,
        },
      ],
    },
  },
};

const COMMERCIAL_CONDITIONS_COPIES = {
  [COMMERCIAL_CONDITIONS_VERSION]: COMMERCIAL_CONDITIONS_COPY_2026_10_23,
};

function resolveCopy(i18n, forcedLanguageCode, requestedVersion) {
  const copy = COMMERCIAL_CONDITIONS_COPIES[requestedVersion || COMMERCIAL_CONDITIONS_VERSION]
    || COMMERCIAL_CONDITIONS_COPY_2026_10_23;
  const explicit = String(forcedLanguageCode || "").trim().toLowerCase();
  if (explicit === "ru") return copy.ru;
  if (explicit === "sk") return copy.sk;
  if (explicit === "en") return copy.en;

  const lang = String(i18n?.resolvedLanguage || i18n?.language || "en").toLowerCase();
  if (lang.startsWith("ru")) return copy.ru;
  if (lang.startsWith("sk") || lang.startsWith("cs")) return copy.sk;
  return copy.en;
}

function formatMoney(value, language, options = {}) {
  const amount = Number(value) || 0;
  const locale = language === "ru" ? "ru-RU" : language === "sk" ? "sk-SK" : "en-GB";
  const currency = "EUR";
  const defaultFractionDigits = 2;
  const {
    minimumFractionDigits = defaultFractionDigits,
    maximumFractionDigits = defaultFractionDigits,
  } = options;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}

function formatTemplate(template, values) {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? "");
}

function buildRange(turnover) {
  if (turnover <= 300) {
    return {
      id: "community",
      commissionRateMin: 0.05,
      commissionRateMax: 0.05,
      minimumPlatformFee: 0,
    };
  }
  if (turnover <= 1000) {
    return {
      id: "growth",
      commissionRateMin: 0.04,
      commissionRateMax: 0.04,
      minimumPlatformFee: 0,
    };
  }
  return {
    id: "enterprise",
    commissionRateMin: 0.03,
    commissionRateMax: 0.03,
    minimumPlatformFee: ENTERPRISE_MIN_PLATFORM_FEE,
  };
}

function TabChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-11 items-center justify-center rounded-full border px-4 text-[0.92rem] font-semibold transition ${
        active
          ? "border-[#5ec8ff]/55 bg-[linear-gradient(90deg,rgba(47,103,231,0.8),rgba(47,216,246,0.72))] text-white shadow-[0_14px_26px_rgba(47,116,232,0.22)]"
          : "border-white/14 bg-white/[0.05] text-white/84 hover:border-[#5ec8ff]/45 hover:bg-white/[0.08]"
      }`}
    >
      {children}
    </button>
  );
}

function TierCard({ card, accentClass }) {
  return (
    <article
      className={`rounded-[28px] border px-5 py-5 shadow-[0_18px_44px_rgba(0,0,0,0.22)] ${accentClass}`}
    >
      <div className="text-[1rem] font-semibold text-white/84">
        {card.badge}
      </div>

      <div className="mt-5 text-[1.45rem] font-extrabold text-white">{card.heading}</div>
      <div className="mt-2 text-[0.92rem] text-white/62">{card.turnover}</div>

      <div className="mt-6">
        <div className="text-[2.25rem] font-black leading-none text-white">{card.commission}</div>
        <div className="mt-2 text-[0.82rem] uppercase tracking-[0.14em] text-white/50">
          {card.commissionLabel}
        </div>
      </div>

      <div className="mt-5 space-y-3 border-t border-white/10 pt-5">
        {card.points.map((point) => (
          <div key={point} className="flex items-start gap-3 text-[0.94rem] leading-[1.55] text-white/80">
            <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-[0.72rem] text-white/74">
              •
            </span>
            <span>{point}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

function MoneyValue({ children, emphasize = false }) {
  return (
    <span className={emphasize ? "text-[1.55rem] font-black leading-none text-[#57e08d]" : "font-semibold text-white"}>
      {children}
    </span>
  );
}

function CommercialConditionsSection({ copy, contentBottomSpace }) {
  const tierAccentClasses = [
    "border-[#42d17b]/38 bg-[linear-gradient(180deg,rgba(19,43,36,0.68),rgba(9,18,24,0.9))]",
    "border-[#4f7cff]/34 bg-[linear-gradient(180deg,rgba(16,34,67,0.74),rgba(9,18,29,0.92))]",
    "border-[#ff7d7d]/28 bg-[linear-gradient(180deg,rgba(58,27,41,0.78),rgba(17,16,29,0.94))]",
  ];
  return (
    <div className="space-y-6 pb-[var(--commercial-bottom-space)]" style={{ "--commercial-bottom-space": contentBottomSpace }}>
      <div className="rounded-[30px] border border-white/10 bg-white/[0.04] px-5 py-5 shadow-[0_22px_50px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
        <div className="grid gap-4 lg:grid-cols-3">
          {copy.terms.cards.map((card, index) => (
            <TierCard
              key={card.heading}
              card={card}
              accentClass={tierAccentClasses[index] || tierAccentClasses[0]}
            />
          ))}
        </div>
        <p className="mt-4 rounded-[18px] border border-[#5ec8ff]/25 bg-[#5ec8ff]/[0.07] px-4 py-3 text-[0.92rem] leading-[1.58] text-white/85">
          {copy.terms.organizerCardFeeNotice}
        </p>
      </div>

    </div>
  );
}

function FeeCalculatorSection({
  copy,
  languageBase,
  ticketPrice,
  participants,
  paymentMethod,
  depositEnabled,
  depositPercent,
  setTicketPrice,
  setParticipants,
  setPaymentMethod,
  setDepositEnabled,
  setDepositPercent,
  contentBottomSpace,
}) {
  const price = Math.max(0, Number(ticketPrice) || 0);
  const ticketCount = Math.max(0, Math.trunc(Number(participants) || 0));
  const grossTurnover = price * ticketCount;
  const activeRange = buildRange(grossTurnover);
  const commissionRate = activeRange.commissionRateMin;
  const rawPlatformCommission = grossTurnover * commissionRate;
  const platformCommission = Math.max(rawPlatformCommission, activeRange.minimumPlatformFee || 0);
  const isCash = paymentMethod === "cash";
  const cardPaymentAmounts = getTicketCardPaymentAmounts(price, depositEnabled ? depositPercent : 100);
  const perTicketProcessing = estimateTicketProcessingTariff(price, depositEnabled ? depositPercent : 100).total;
  const estimatedProcessingTariff = isCash
    ? 0
    : perTicketProcessing * ticketCount;
  const estimatedPayout = Math.max(0, grossTurnover - platformCommission - estimatedProcessingTariff);
  const rangeLabel = copy.calculator.rangeLabels[activeRange.id];
  const money = (amount) => formatMoney(amount, languageBase);
  const commissionCalculation = formatTemplate(
    rawPlatformCommission < activeRange.minimumPlatformFee
      ? copy.calculator.commissionMinimumFormula
      : copy.calculator.commissionFormula,
    {
      rate: commissionRate * 100,
      turnover: money(grossTurnover),
      calculated: money(rawPlatformCommission),
      minimum: money(activeRange.minimumPlatformFee),
    }
  );
  const processingCalculation = isCash
    ? copy.calculator.processingCashFormula
    : depositEnabled
      ? formatTemplate(copy.calculator.processingSplit, {
          count: ticketCount,
          depositFee: money(estimateOrganizerProcessingTariff(cardPaymentAmounts[0])),
          balanceFee: money(estimateOrganizerProcessingTariff(cardPaymentAmounts[1])),
        })
      : formatTemplate(copy.calculator.processingSingle, {
          count: ticketCount,
          perTicket: money(perTicketProcessing),
        });
  const modelTone = {
    community: {
      cardClass: "border-[#42d17b]/28 bg-[linear-gradient(180deg,rgba(19,43,36,0.62),rgba(12,22,20,0.9))]",
      labelClass: "text-[#9af0bc]",
      amountClass: "text-[#8ff0b5]",
    },
    growth: {
      cardClass: "border-[#4f7cff]/28 bg-[linear-gradient(180deg,rgba(17,36,72,0.7),rgba(12,19,34,0.92))]",
      labelClass: "text-[#9ec4ff]",
      amountClass: "text-[#8ec2ff]",
    },
    enterprise: {
      cardClass: "border-[#ff7d7d]/26 bg-[linear-gradient(180deg,rgba(58,27,41,0.74),rgba(22,16,25,0.94))]",
      labelClass: "text-[#ffb5b5]",
      amountClass: "text-[#ffb1b1]",
    },
  }[activeRange.id];

  return (
    <div
      className="grid gap-5 pb-[var(--commercial-bottom-space)] lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)]"
      style={{ "--commercial-bottom-space": contentBottomSpace }}
    >
      <div className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,26,40,0.94),rgba(9,16,25,0.94))] px-5 py-5 shadow-[0_22px_50px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
        <div className="grid gap-4">
          <label className="rounded-[22px] border border-white/10 bg-white/[0.04] px-4 py-4">
            <div className="text-[0.88rem] font-semibold text-white/72">{copy.calculator.ticketPrice}</div>
            <input
              type="number"
              min="0"
              step="0.5"
              value={ticketPrice}
              onChange={(event) => setTicketPrice(event.target.value)}
              className="mt-3 h-12 w-full rounded-[16px] border border-white/12 bg-[#111b2a] px-4 text-[1.2rem] font-semibold text-white outline-none placeholder:text-white/28"
            />
          </label>

          <label className="rounded-[22px] border border-white/10 bg-white/[0.04] px-4 py-4">
            <div className="text-[0.88rem] font-semibold text-white/72">{copy.calculator.participants}</div>
            <input
              type="number"
              min="1"
              step="1"
              value={participants}
              onChange={(event) => setParticipants(event.target.value)}
              className="mt-3 h-12 w-full rounded-[16px] border border-white/12 bg-[#111b2a] px-4 text-[1.2rem] font-semibold text-white outline-none placeholder:text-white/28"
            />
          </label>

          <div className="rounded-[22px] border border-white/10 bg-white/[0.04] px-4 py-4">
            <div className="text-[0.88rem] font-semibold text-white/72">{copy.calculator.paymentMethod}</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {[
                { id: "card", label: copy.calculator.card },
                { id: "cash", label: copy.calculator.cash },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setPaymentMethod(option.id)}
                  className={`h-12 rounded-[16px] border text-[0.94rem] font-semibold transition ${
                    paymentMethod === option.id
                      ? "border-[#5ec8ff]/55 bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] text-white shadow-[0_14px_26px_rgba(47,116,232,0.26)]"
                      : "border-white/12 bg-[#111b2a] text-white/78"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {!isCash && (
            <div className="rounded-[22px] border border-white/10 bg-white/[0.04] px-4 py-4">
              <div className="text-[0.88rem] font-semibold text-white/72">{copy.calculator.depositChoice}</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {[
                  { enabled: false, label: copy.calculator.onePayment },
                  { enabled: true, label: copy.calculator.twoPayments },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={depositEnabled === option.enabled}
                    onClick={() => setDepositEnabled(option.enabled)}
                    className={`min-h-12 rounded-[16px] border px-2 text-[0.86rem] font-semibold transition ${
                      depositEnabled === option.enabled
                        ? "border-[#5ec8ff]/55 bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] text-white shadow-[0_14px_26px_rgba(47,116,232,0.26)]"
                        : "border-white/12 bg-[#111b2a] text-white/78"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {depositEnabled && (
                <div className="mt-4">
                  <label htmlFor="commercial-deposit-percent" className="flex items-center justify-between gap-3 text-[0.88rem] font-semibold text-white/72">
                    <span>{copy.calculator.depositPercent}</span>
                    <span className="text-white">{depositPercent} %</span>
                  </label>
                  <input
                    id="commercial-deposit-percent"
                    type="range"
                    min="10"
                    max="90"
                    step="10"
                    value={depositPercent}
                    onChange={(event) => setDepositPercent(Number(event.target.value))}
                    className="mt-3 w-full accent-[#5ec8ff]"
                  />
                  <p className="mt-2 text-[0.82rem] leading-[1.45] text-white/58">
                    {formatTemplate(copy.calculator.depositSplit, {
                      deposit: money(cardPaymentAmounts[0]),
                      balance: money(cardPaymentAmounts[1]),
                    })}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,26,40,0.94),rgba(9,16,25,0.94))] px-5 py-5 shadow-[0_22px_50px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
        <div className={`rounded-[22px] border px-4 py-4 ${modelTone.cardClass}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="text-[0.9rem] font-semibold text-white/66">{copy.calculator.selectedModel}</div>
            <div className={`text-[1rem] font-semibold ${modelTone.labelClass}`}>
              {rangeLabel}
            </div>
          </div>
          <div className={`mt-4 text-[2rem] font-black leading-none ${modelTone.amountClass}`}>
            {formatMoney(grossTurnover, languageBase)}
          </div>
          <div className="mt-2 text-[0.88rem] text-white/56">{copy.calculator.turnover}</div>
        </div>

        <div className="mt-5 space-y-4 rounded-[22px] border border-white/10 bg-black/20 px-4 py-4">
          <div className="flex items-center justify-between gap-4 text-[0.96rem] text-white/76">
            <span>{copy.calculator.platformCommission}</span>
            <div className="max-w-[11rem] text-right">
              <MoneyValue>{money(platformCommission)}</MoneyValue>
            </div>
          </div>
          <div className="text-[0.82rem] leading-[1.45] text-white/58">{commissionCalculation}</div>
          <div className="flex items-center justify-between gap-4 text-[0.96rem] text-white/76">
            <span>{copy.calculator.serviceFee}</span>
            <div className="max-w-[11rem] text-right">
              <MoneyValue>{formatMoney(estimatedProcessingTariff, languageBase)}</MoneyValue>
            </div>
          </div>
          {!isCash && (
            <div className="text-[0.82rem] leading-[1.45] text-white/58">{copy.calculator.processingFormula}</div>
          )}
          <div className="text-[0.82rem] leading-[1.45] text-white/58">{processingCalculation}</div>
          <div className="h-px bg-white/10" />
          <div className="flex items-center justify-between gap-4">
            <span className="text-[1rem] font-semibold text-white">{copy.calculator.estimatedPayout}</span>
            <MoneyValue emphasize>{money(estimatedPayout)}</MoneyValue>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefundPolicyCard({ policy, accentClass }) {
  return (
    <article className={`rounded-[28px] border px-5 py-5 shadow-[0_18px_44px_rgba(0,0,0,0.22)] ${accentClass}`}>
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[1.2rem] font-extrabold text-white">{policy.label}</div>
          </div>
          <span className="inline-flex shrink-0 whitespace-nowrap px-3 py-1 text-[0.74rem] font-semibold uppercase tracking-[0.12em] text-white/78">
            {policy.badge}
          </span>
        </div>

        <div className="mt-2 max-w-none text-[0.92rem] leading-[1.55] text-white/66">
          {policy.summary}
        </div>
      </div>

      <div className="mt-5 space-y-3 border-t border-white/10 pt-5">
        {policy.rules.map((rule, index) => (
          <div key={`${policy.id}-${index}`} className="rounded-[18px] bg-black/18 px-4 py-3">
            <div
              className={`text-[0.8rem] font-semibold uppercase tracking-[0.08em] ${
                rule.outcome ? "text-white/50" : "text-white"
              }`}
            >
              {rule.condition}
            </div>
            {rule.outcome ? (
              <div className="mt-2 text-[1rem] font-semibold text-white">{rule.outcome}</div>
            ) : null}
          </div>
        ))}
      </div>
    </article>
  );
}

function RefundPoliciesSection({ copy, contentBottomSpace }) {
  const refundAccentClasses = [
    "border-white/14 bg-[linear-gradient(180deg,rgba(19,43,36,0.62),rgba(12,22,20,0.92))]",
    "border-white/14 bg-[linear-gradient(180deg,rgba(17,36,72,0.7),rgba(12,19,34,0.92))]",
    "border-white/14 bg-[linear-gradient(180deg,rgba(58,27,41,0.74),rgba(22,16,25,0.94))]",
  ];

  return (
    <div className="space-y-6 pb-[var(--commercial-bottom-space)]" style={{ "--commercial-bottom-space": contentBottomSpace }}>
      <div className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,26,40,0.94),rgba(9,16,25,0.94))] px-5 py-5 shadow-[0_22px_50px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
        <div className="text-[1.08rem] font-semibold text-white">{copy.refunds.title}</div>
        <p className="mt-2 max-w-[920px] text-[0.92rem] leading-[1.6] text-white/68">{copy.refunds.intro}</p>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          {copy.refunds.options.map((policy, index) => (
            <RefundPolicyCard
              key={policy.id}
              policy={policy}
              accentClass={refundAccentClasses[index] || refundAccentClasses[0]}
            />
          ))}
        </div>
      </div>

      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-4 text-[0.92rem] leading-[1.6] text-white/68 shadow-[0_18px_44px_rgba(0,0,0,0.18)] sm:px-6">
        {copy.refunds.note}
      </div>
    </div>
  );
}

function HowItWorksPaymentCard({ note }) {
  return (
    <div className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,26,40,0.94),rgba(9,16,25,0.94))] px-5 py-5 shadow-[0_22px_50px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center self-start rounded-2xl border border-white/10 bg-white/[0.06] text-[#67c9ff]">
          <note.Icon />
        </div>
        <div className="min-w-0">
          <div className="text-[1rem] font-semibold text-white">{note.title}</div>
          <div className="mt-2 text-[0.92rem] leading-[1.58] text-white/68">{note.body}</div>
        </div>
      </div>
    </div>
  );
}

function HowItWorksSection({ copy, contentBottomSpace }) {
  return (
    <div
      className="grid gap-5 pb-[var(--commercial-bottom-space)] lg:grid-cols-[minmax(0,1fr)_minmax(0,0.86fr)]"
      style={{ "--commercial-bottom-space": contentBottomSpace }}
    >
      <div className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,26,40,0.94),rgba(9,16,25,0.94))] px-5 py-5 shadow-[0_22px_50px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
        <div className="space-y-4">
          {copy.howItWorks.steps.map((step, index) => (
            <div key={step.title} className="rounded-[24px] border border-white/10 bg-white/[0.04] px-4 py-4">
              <div className="flex items-start gap-4">
                <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#4f80dc]/58 bg-[radial-gradient(circle_at_30%_22%,rgba(83,139,255,0.36),rgba(9,24,56,0.95)_58%)] text-[#5db8ff] shadow-[0_0_24px_rgba(69,131,255,0.24)]">
                  <step.Icon />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white/[0.08] px-2 text-[0.8rem] font-semibold text-white/74">
                      {index + 1}
                    </span>
                    <div className="text-[1.02rem] font-semibold text-white">{step.title}</div>
                  </div>
                  <div className="mt-3 text-[0.92rem] leading-[1.62] text-white/68">{step.body}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-5">
        {copy.howItWorks.paymentNotes.map((note) => (
          <HowItWorksPaymentCard key={note.title} note={note} />
        ))}
      </div>
    </div>
  );
}

function CommercialConditionsPage({ forcedLanguageCode, isGuest = false }) {
  const { i18n } = useTranslation("events");
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const requestedVersion = new URLSearchParams(location.search || "").get("version");
  const copy = useMemo(() => resolveCopy(i18n, forcedLanguageCode, requestedVersion), [i18n, forcedLanguageCode, requestedVersion]);
  const languageBase = useMemo(() => {
    const explicit = String(forcedLanguageCode || "").trim().toLowerCase();
    if (explicit === "ru" || explicit === "sk" || explicit === "en") return explicit;
    const lang = String(i18n?.resolvedLanguage || i18n?.language || "en").toLowerCase();
    if (lang.startsWith("ru")) return "ru";
    if (lang.startsWith("sk")) return "sk";
    return "en";
  }, [forcedLanguageCode, i18n]);

  const [ticketPrice, setTicketPrice] = useState(DEFAULT_TICKET_PRICE);
  const [participants, setParticipants] = useState(DEFAULT_PARTICIPANTS);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [depositPercent, setDepositPercent] = useState(50);
  const [activeSection, setActiveSection] = useState("terms");
  const [displayedSection, setDisplayedSection] = useState("terms");
  const [panelVisible, setPanelVisible] = useState(true);

  useEffect(() => {
    const raw = String(forcedLanguageCode || "").trim().toLowerCase();
    if (!raw) return undefined;
    if (!["en", "ru", "sk"].includes(raw)) return undefined;

    const previousLanguage = i18n.resolvedLanguage || i18n.language || "";
    const currentBase = String(i18n.language || "").toLowerCase().split("-")[0];
    const shouldSwitch = currentBase !== raw;

    if (shouldSwitch) {
      Promise.resolve(i18n.changeLanguage(raw)).catch(() => {});
    }

    return () => {
      if (!shouldSwitch || !previousLanguage) return;
      Promise.resolve(i18n.changeLanguage(previousLanguage)).catch(() => {});
    };
  }, [forcedLanguageCode, i18n]);

  useEffect(() => {
    if (activeSection === displayedSection) {
      setPanelVisible(true);
      return undefined;
    }

    setPanelVisible(false);
    const switchTimer = window.setTimeout(() => {
      setDisplayedSection(activeSection);
      setPanelVisible(true);
    }, TRANSITION_DURATION_MS);

    return () => window.clearTimeout(switchTimer);
  }, [activeSection, displayedSection]);

  const contentBottomSpace = isDesktop
    ? "120px"
    : "calc(var(--bottom-nav-height, 72px) + env(safe-area-inset-bottom, 0px) + 136px)";
  const returnTo = String(location.state?.returnTo || "").trim();
  const returnState = location.state?.returnState;

  const handleClose = () => {
    if (returnTo) {
      navigate(returnTo, { replace: true, state: returnState });
      return;
    }
    navigate(isGuest ? "/" : "/events/create");
  };

  const renderedSection = (() => {
    if (displayedSection === "calculator") {
      return (
        <FeeCalculatorSection
          copy={copy}
          languageBase={languageBase}
          ticketPrice={ticketPrice}
          participants={participants}
          paymentMethod={paymentMethod}
          depositEnabled={depositEnabled}
          depositPercent={depositPercent}
          setTicketPrice={setTicketPrice}
          setParticipants={setParticipants}
          setPaymentMethod={setPaymentMethod}
          setDepositEnabled={setDepositEnabled}
          setDepositPercent={setDepositPercent}
          contentBottomSpace={contentBottomSpace}
        />
      );
    }
    if (displayedSection === "refunds") {
      return <RefundPoliciesSection copy={copy} contentBottomSpace={contentBottomSpace} />;
    }
    if (displayedSection === "howItWorks") {
      return <HowItWorksSection copy={copy} contentBottomSpace={contentBottomSpace} />;
    }
    return <CommercialConditionsSection copy={copy} contentBottomSpace={contentBottomSpace} />;
  })();

  if (requestedVersion && !Object.prototype.hasOwnProperty.call(COMMERCIAL_CONDITIONS_COPIES, requestedVersion)) {
    return <div role="alert" className="px-5 py-8 text-white">
      {languageBase === "ru" ? "Запрошенная редакция Коммерческих условий недоступна." : languageBase === "sk" ? "Požadované znenie obchodných podmienok nie je dostupné." : "The requested Commercial Conditions edition is unavailable."}
    </div>;
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto overscroll-contain bg-[radial-gradient(circle_at_top,rgba(23,67,131,0.36),transparent_34%),linear-gradient(180deg,#050b16_0%,#08111f_42%,#07101a_100%)] text-white [-webkit-overflow-scrolling:touch]">
      <div className="mx-auto flex min-h-full w-full max-w-[1180px] flex-col px-4 pt-[calc(var(--vg-safe-area-top)+18px)] sm:px-6 lg:px-8">
        <div className="flex items-start justify-between gap-4">
          <div className="max-w-[760px]">
            <div className="text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-[#6FD3FF]">VaultGrid</div>
            <h1 className="mt-3 text-[2rem] font-black leading-[1.02] text-white sm:text-[2.6rem]">{copy.title}</h1>
            <p className="mt-2 text-xs font-semibold text-white/55">{languageBase === "ru" ? "Редакция" : languageBase === "sk" ? "Znenie" : "Edition"} {requestedVersion || COMMERCIAL_CONDITIONS_VERSION}</p>
            <p className="mt-4 max-w-[760px] text-[1rem] leading-[1.68] text-white/72 sm:text-[1.04rem]">
              {copy.subtitle}
            </p>
          </div>

          <VaultModalCloseButton
            onClick={handleClose}
            aria-label={copy.closeAria}
            iconClassName="h-10 w-10 sm:h-11 sm:w-11"
          />
        </div>

        <p className="mt-5 rounded-[18px] border border-cyan-300/20 bg-cyan-300/[0.07] px-4 py-4 text-sm leading-6 text-white/78">
          {copy.directModeNote}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <TabChip active={activeSection === "terms"} onClick={() => setActiveSection("terms")}>
            {copy.nav.terms}
          </TabChip>
          <TabChip active={activeSection === "calculator"} onClick={() => setActiveSection("calculator")}>
            {copy.nav.calculator}
          </TabChip>
          <TabChip active={activeSection === "refunds"} onClick={() => setActiveSection("refunds")}>
            {copy.nav.refunds}
          </TabChip>
          <TabChip active={activeSection === "howItWorks"} onClick={() => setActiveSection("howItWorks")}>
            {copy.nav.howItWorks}
          </TabChip>
        </div>

        <section className="mt-8">
          <div className="mb-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/12" />
            <div className="text-[0.8rem] font-semibold uppercase tracking-[0.18em] text-white/46">
              {activeSection === "terms"
                ? copy.terms.title
                : activeSection === "calculator"
                  ? copy.calculator.title
                  : activeSection === "refunds"
                    ? copy.refunds.title
                    : copy.howItWorks.title}
            </div>
            <div className="h-px flex-1 bg-white/12" />
          </div>

          <div
            className={`transition-all duration-200 ${
              panelVisible ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
            }`}
          >
            {renderedSection}
          </div>
        </section>
      </div>
    </div>
  );
}

export default CommercialConditionsPage;
